import {DestroyRef, Injectable} from '@angular/core';
import {isDefined, isUndefined} from './data-util';
import {TraitsService} from './traits.service';
import {LookupService} from './lookup.service';
import {NgEventBus} from 'ng-event-bus';
import {PlanMessages} from '../models/plan-messages.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
@Injectable({
    providedIn: 'root'
})
export class MessageService {
    title = 'Message Service';
    planMessages: PlanMessages[];

    constructor(
        private eventBus: NgEventBus,
        private traitsService: TraitsService,
        private lookupService: LookupService,
        private destroyRef: DestroyRef
    ) {
        if (this.lookupService.lookupsLoaded) {
            this.planMessages = this.lookupService.getLookups('planMessages');
        } else {
            this.eventBus
                .on('nlr:lookups-loaded')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.planMessages = this.lookupService.getLookups('planMessages');
                });
        }
    }

    // $rootScope.$on('traitsChanged', function() {
    //     $rootScope.$broadcast('messagesChanged');
    // });

    /**
     * Gets a message matching a key, if it is applicable to the current plan, and formats the string given the placeholders in the tokenValues map.
     * If multiple messages match, the one with the lowest numeric priority value will be chosen.
     *
     * @param key The key of the message to find.
     * @param tokenValues A map of (expected) message placeholder values to substitution values, see PlanMessageLookup.valueFormat for placeholders.
     * @returns {string|*} The formatted message, or an empty string if there were no matches.
     */
    getMessage(key, tokenValues) {
        const applicableMessages = this.findApplicableMessages(key);
        if (isUndefined(applicableMessages) || applicableMessages.length === 0) {
            return '';
        }

        // Lower priority numbers take precedent over higher priority numbers.
        const planMessage = applicableMessages.reduce((prev, curr) => (prev.priority < curr.priority ? prev : curr));

        // If no substitution is required, just return the message's valueFormat.
        if (isUndefined(tokenValues) || tokenValues.length === 0) {
            return planMessage.valueFormat;
        }

        // Format the message by substituting message format keys for their respective values.
        let formattedMessageValue = planMessage.valueFormat;

        tokenValues.forEach((substitution, placeholder) => {
            const formatKey = '${' + placeholder + '}';
            formattedMessageValue = formattedMessageValue.replace(formatKey, substitution);
        });

        return formattedMessageValue;
    }

    /**
     * Determines whether a message key is applicable to the current plan lodgement.
     * This only counts messages that have a non-blank valueFormat value (PlanMessageLookup.valueFormat).
     *
     * i.e. Whether it exists in the plan message lookup configuration table (and is configured to be 'active'),
     * and applies to the current plan lodgement, based on the plan's known traits.
     *
     * @param key The message key (see PlanMessageLookup.key).
     * @returns {boolean|boolean|*} Whether or not the message is applicable, or undefined if traits aren't initialised.
     */
    isMessageDefined(key) {
        const messages = this.findApplicableMessages(key);
        return messages !== undefined
            ? messages.some(planMessageLookup => isDefined(planMessageLookup.valueFormat) && planMessageLookup.valueFormat !== '')
            : undefined;
    }

    /**
     * Determines which messages matching a given key are applicable to the current plan lodgement.
     *
     * i.e. All messages that exist in the plan message lookup configuration table (and are configured to be 'active'),
     * and apply to the current plan lodgement, based on the plan's known traits.
     *
     * @param key The message key.
     * @returns PlanMessageLookup[] A list of the applicable messages, or an empty list if none are, or undefined if traits aren't initialised.
     */
    findApplicableMessages(key) {
        if (!this.traitsService.hasTraits()) {
            return undefined;
        }
        return this.planMessages.filter(
            planMessageLookup =>
                planMessageLookup.key === key &&
                planMessageLookup.active === true &&
                planMessageLookup.planTraitCriteria.every(planTraitCriterion => this.traitsService.traits.includes(planTraitCriterion))
        );
    }
}
